import { type FC, useCallback, useRef } from "react";
import { Col, HeadingLevel, Row } from "@viagogo/core/ui";
import { AddFeature, RecordParticipationTypes, RemoveFeature } from "@viagogo/feature";
import type { CategoryEventListViewModel } from "src/http/api-types";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { useAppContext } from "src/shared/context/AppContext";
import type { Location } from "src/types/shared/cookies";
import { EmailSignUp } from "src/website-apps/shared/components/email-signup";
import { EventListRecentlyViewedGrid } from "src/website-apps/shared/components/event-list-recently-viewed-grid";
import { FiltersAndGridsManager } from "src/website-apps/shared/components/FiltersAndGridsManager";
import { UserEventToastOnAncillaryPages } from "src/website-apps/shared/components/user-event-toast";
import { canShowWorldCupFanProtectDisplay } from "src/website-apps/shared/components/world-cup-fan-protect/eligibility";
import { eventListPlacement } from "src/website-apps/shared/components/world-cup-fan-protect/styles";
import { WorldCupFanProtectDisplay } from "src/website-apps/shared/components/world-cup-fan-protect/WorldCupFanProtectDisplay";
import { selectReducerGrids } from "src/website-apps/shared/utils/selectReducerGrids";
import { MaybeVenuesLinksGrid } from "../LayoutBody/MaybeVenuesLinksGrid";
import { BodyWrapper, FlushContainer, MarginDivLarge, VerticalMarginRow } from "../LayoutBody/styled-components";
import { EventsNearYou } from "./EventsNearYou";
import { HeroHeader } from "./HeroHeader";
import { TeamsNearYou } from "./TeamsNearYou";
import { TeamsSection } from "./TeamsSection";

interface Props {
  location: Maybe<Location>;
  model: CategoryEventListViewModel;
  setLocation: (location: Location) => void;
}

/**
 * New Version of the LayoutBody Component for Applicable Categories
 */
export const ImmersiveLayoutBody: FC<Props> = ({ location, model, setLocation }) => {
  const appContext = useAppContext();
  const teamsScrollRef = useRef<HTMLElement | null>(null);
  const { categoryId, categoryName, categoryTitle } = model.categorySummary ?? {};
  const canShowWorldCupFanProtect = canShowWorldCupFanProtectDisplay(categoryId, appContext.cobrandId);

  // OnClick Handler to Scroll Down to Teams Section
  const scrollToTeamsGrid = useCallback(() => {
    teamsScrollRef.current?.scrollIntoView({
      behavior: "smooth",
    });
  }, []);

  return (
    <>
      <UserEventToastOnAncillaryPages userEventOption={model.userEventOption} />
      <RemoveFeature
        id={FeatureSetEnum.TMGlobalImmersiveHeaderOnAncillaryPages}
        feature={() => (
          <HeadingLevel>
            <HeroHeader
              categoryId={categoryId}
              leagueContent={model.leagueHeaderContent}
              logoUrl={model.leagueLogoImageUrl}
              onClick={scrollToTeamsGrid}
              title={categoryTitle ?? undefined}
            />
          </HeadingLevel>
        )}
      />
      <BodyWrapper $secondaryBgColor>
        <FlushContainer>
          <Row between={[true]}>
            <Col span={[12]}>
              <TeamsNearYou categoryId={categoryId} setUserLocation={setLocation} userLocation={location} />
              <AddFeature
                id={FeatureSetEnum.CategoryGroupingEventsNearYou}
                canParticipate={() => !!selectReducerGrids(model.eventGrids).primaryGrid?.items?.length}
                recordType={RecordParticipationTypes.IN_VIEW}
                feature={() => <EventsNearYou categoryId={categoryId} />}
              />
              <TeamsSection categoryName={categoryName} ref={teamsScrollRef} teams={model.performers?.items ?? []} />
              <AddFeature
                id={FeatureSetEnum.TMWorldCupFanProtectDisplay}
                recordType={RecordParticipationTypes.IN_VIEW}
                canParticipate={() => canShowWorldCupFanProtect}
                feature={() => <WorldCupFanProtectDisplay mode="responsive" className={eventListPlacement} />}
              />
              <FiltersAndGridsManager filterTypeOptions={model.gridFilterTypes} />
              <MarginDivLarge>
                <MaybeVenuesLinksGrid
                  isGrouping={model.isGrouping}
                  categoryName={categoryName}
                  venues={model.venues ?? []}
                />
              </MarginDivLarge>
            </Col>
          </Row>
          <Row>
            <EventListRecentlyViewedGrid />
          </Row>
          <VerticalMarginRow>
            {!appContext.isNativeApp && (
              <Col span={[12]}>
                <EmailSignUp emailPrivacyDisclosureSiteMessage={model.privacyPolicyMessage} />
              </Col>
            )}
          </VerticalMarginRow>
        </FlushContainer>
      </BodyWrapper>
    </>
  );
};
